import { useState, useRef, useCallback } from 'react';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import { bulkImportCompensations } from '../../hooks/useFirestore';
import { useToast } from '../../contexts/ToastContext';
import { cn } from '../../utils/cn';
import { isEmail, normalizeEmail } from '../../utils/hierarchy';
import { computedTotalCash } from '../../utils/compensation';

/* ── CSV column → compensation field mapping ── */
const FIELD_MAP = {
  employeename:       'employeeName',
  employee_name:      'employeeName',
  name:               'employeeName',
  employeeemail:      'employeeEmail',
  employee_email:     'employeeEmail',
  email:              'employeeEmail',
  department:         'department',
  communicatoremail:  'communicatorEmail',
  communicator_email: 'communicatorEmail',
  communicatorname:   'communicatorName',
  communicator_name:  'communicatorName',
  category:           'category',
  currency:           'currency',
  paytype:            'payType',
  pay_type:           'payType',
  currenttitle:       'currentTitle',
  current_title:      'currentTitle',
  newtitle:           'newTitle',
  new_title:          'newTitle',
  currentbase:        'currentBase',
  current_base:       'currentBase',
  newbase:            'newBase',
  new_base:           'newBase',
  currentvariabletarget: 'currentVariableTarget',
  current_variable_target: 'currentVariableTarget',
  newvariabletarget:  'newVariableTarget',
  new_variable_target: 'newVariableTarget',
  currenttotalcash:   'totalCurrentComp',
  current_total_cash: 'totalCurrentComp',
  totalcurrentcomp:   'totalCurrentComp',
  total_current_comp: 'totalCurrentComp',
  newtotalcash:       'totalNewComp',
  new_total_cash:     'totalNewComp',
  totalnewcomp:       'totalNewComp',
  total_new_comp:     'totalNewComp',
  effectivedate:      'effectiveDate',
  effective_date:     'effectiveDate',
  paydate:            'payDate',
  pay_date:           'payDate',
  variabledisplaytype: 'variableDisplayType',
  variable_display_type: 'variableDisplayType',
  notes:              'notes',
  message:            'message',
};

/**
 * Parse a column name to detect grant fields.
 * Supports: grantType, grant_type, grant1Type, grant_1_type, grant1_type, etc.
 * Returns { index: number, field: string } or null if not a grant column.
 */
function parseGrantColumn(colName) {
  const normalized = colName.toLowerCase().replace(/[\s-]/g, '');
  // Match patterns like: grant1type, grant_1_type, granttype (index 0), grant1_type
  const match = normalized.match(/^grant_?(\d+)?_?([a-z_]+)$/);
  if (!match) return null;
  const index = match[1] ? parseInt(match[1], 10) - 1 : 0; // grant1 = index 0, grantType (no number) = index 0
  const suffix = match[2].replace(/_/g, '');
  // Map suffix to field name
  const fieldMap = {
    type: 'type',
    category: 'grantCategory',
    units: 'units',
    value: 'value',
    vestingperiod: 'vestingPeriod',
    cliff: 'cliff',
    vestingtext: 'vestingText',
  };
  const field = fieldMap[suffix];
  if (!field) return null;
  return { index, field };
}

const NUMERIC_FIELDS = new Set([
  'currentBase', 'newBase', 'currentVariableTarget', 'newVariableTarget',
  'totalCurrentComp', 'totalNewComp',
]);

const CSV_TEMPLATE_HEADERS = [
  'employeeName', 'employeeEmail', 'department',
  'communicatorEmail', 'communicatorName',
  'category', 'currency', 'payType',
  'currentTitle', 'newTitle',
  'currentBase', 'newBase',
  'currentVariableTarget', 'newVariableTarget',
  'currentTotalCash', 'newTotalCash',
  'variableDisplayType',
  'effectiveDate', 'payDate', 'notes', 'message',
  // Multiple grants: grant1 columns, grant2 columns, etc.
  'grant1Type', 'grant1Category', 'grant1Units', 'grant1VestingPeriod', 'grant1Cliff', 'grant1VestingText',
  'grant2Type', 'grant2Category', 'grant2Units', 'grant2VestingPeriod', 'grant2Cliff', 'grant2VestingText',
];

/* ── Helpers ── */
function parseCsvLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { current += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { current += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { result.push(current.trim()); current = ''; }
      else { current += ch; }
    }
  }
  result.push(current.trim());
  return result;
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = parseCsvLine(lines[0]);
  const rows = lines.slice(1).map((line) => {
    const vals = parseCsvLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  });
  return { headers, rows };
}

function mapRow(raw) {
  const mapped = {
    equityGrants: [],
  };
  const grants = {}; // { 0: { type, units, ... }, 1: { ... }, ... }

  for (const [csvCol, value] of Object.entries(raw)) {
    if (!value) continue;
    
    // Check if it's a grant column first
    const grantInfo = parseGrantColumn(csvCol);
    if (grantInfo) {
      if (!grants[grantInfo.index]) {
        grants[grantInfo.index] = {};
      }
      grants[grantInfo.index][grantInfo.field] = value;
      continue;
    }
    
    // Regular field mapping
    const key = FIELD_MAP[csvCol.toLowerCase().replace(/[\s-]/g, '')];
    if (!key) continue;
    if (NUMERIC_FIELDS.has(key)) {
      mapped[key] = Number(value) || 0;
    } else {
      mapped[key] = value;
    }
  }

  // Build equity grants array from collected grant data
  const grantIndices = Object.keys(grants).map(Number).sort((a, b) => a - b);
  for (const idx of grantIndices) {
    const g = grants[idx];
    if (g.type || g.units) {
      mapped.equityGrants.push({
        type: g.type || 'RSU',
        grantCategory: g.grantCategory || 'refresh',
        units: Number(g.units) || 0,
        vestingPeriod: g.vestingPeriod || '4 years',
        cliff: g.cliff || '1 year',
        ...(g.vestingText ? { vestingText: g.vestingText } : {}),
      });
    }
  }

  // Default currency, payType & variableDisplayType
  if (!mapped.currency) mapped.currency = 'USD';
  mapped.payType = mapped.payType ? String(mapped.payType).trim().toLowerCase() : 'annual';
  if (!mapped.category) mapped.category = 'promotion';
  if (!mapped.variableDisplayType) mapped.variableDisplayType = 'amount';
  // Access rules compare these to the sign-in email exactly.
  if (mapped.employeeEmail) mapped.employeeEmail = normalizeEmail(mapped.employeeEmail);
  if (mapped.communicatorEmail) mapped.communicatorEmail = normalizeEmail(mapped.communicatorEmail);

  // Imported totals win; blank ones are filled in on an annual basis.
  if (mapped.totalCurrentComp == null) mapped.totalCurrentComp = computedTotalCash(mapped, 'current');
  if (mapped.totalNewComp == null) mapped.totalNewComp = computedTotalCash(mapped, 'new');

  return mapped;
}

function downloadTemplate() {
  const csv = CSV_TEMPLATE_HEADERS.join(',') + '\n'
    // Example row with 2 grants (promotion grant + performance grant)
    + 'Jane Doe,jane@forter.com,Engineering,manager@forter.com,Manager Name,promotion,USD,annual,Senior Engineer,Staff Engineer,185000,220000,30000,40000,215000,260000,amount,2026-03-01,"Apr 30, 2026 paycheck",Internal notes,Congratulations!,RSU,promotion,300,4 years,1 year,,RSU,performance,150,4 years,1 year,\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'compensation_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Component ── */
export default function CsvImportModal({ open, onClose, cycleId }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { headers, rows, mapped }
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null); // { created, updated, errors }

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setResult(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const processFile = useCallback((f) => {
    if (!f || !f.name.endsWith('.csv')) {
      toast.error('Please upload a .csv file');
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCsv(e.target.result);
      if (rows.length === 0) {
        toast.error('CSV is empty or has no data rows');
        return;
      }
      const mapped = rows.map(mapRow);
      const valid = mapped.filter((r) => r.employeeName && isEmail(r.employeeEmail) && (!r.communicatorEmail || isEmail(r.communicatorEmail)));
      const invalid = mapped.length - valid.length;
      setPreview({ headers, rows: valid, totalParsed: mapped.length, invalidCount: invalid });
    };
    reader.readAsText(f);
  }, [toast]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    processFile(f);
  }, [processFile]);

  const handleImport = async () => {
    if (!preview?.rows?.length) return;
    setImporting(true);
    try {
      const res = await bulkImportCompensations(cycleId, preview.rows);
      setResult(res);
      if (res.errors.length === 0) {
        toast.success(`Imported ${res.created} new, updated ${res.updated} existing`);
      } else {
        toast.error(`Import completed with ${res.errors.length} error(s)`);
      }
    } catch (e) {
      console.error(e);
      toast.error('Import failed: ' + e.message);
    }
    setImporting(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Import Compensations from CSV"
      size="xl"
      footer={
        <div className="flex items-center justify-between">
          <button onClick={downloadTemplate} className="text-sm text-accent-600 hover:text-accent-700 font-medium flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
            Download Template
          </button>
          <div className="flex gap-3">
            {result ? (
              <Button onClick={handleClose}>Done</Button>
            ) : (
              <>
                <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                <Button onClick={handleImport} loading={importing} disabled={!preview?.rows?.length}>
                  Import {preview?.rows?.length ? `${preview.rows.length} Row${preview.rows.length === 1 ? '' : 's'}` : ''}
                </Button>
              </>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Upload zone */}
        {!preview && !result && (
          <div
            className={cn(
              'border-2 border-dashed rounded-2xl p-10 text-center transition-colors cursor-pointer',
              dragOver ? 'border-accent-500 bg-accent-50' : 'border-surface-300 hover:border-accent-400 hover:bg-surface-50',
            )}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => processFile(e.target.files?.[0])}
            />
            <div className="flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center">
                <svg className="w-7 h-7 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-navy-900">Drop a CSV file here or click to browse</p>
                <p className="text-xs text-navy-400 mt-1">Columns are auto-mapped by header name</p>
              </div>
            </div>
          </div>
        )}

        {/* Preview */}
        {preview && !result && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-navy-900">
                  {file?.name} — {preview.rows.length} valid row{preview.rows.length === 1 ? '' : 's'}
                </p>
                {preview.invalidCount > 0 && (
                  <p className="text-xs text-amber-600">{preview.invalidCount} row(s) skipped (missing name, or invalid employee or communicator email)</p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={reset} className="ml-auto">Change File</Button>
            </div>

            {/* Table preview (first 10 rows) */}
            <div className="overflow-x-auto rounded-xl border border-surface-200">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-surface-50 border-b border-surface-200">
                    <th className="px-3 py-2 text-left font-semibold text-navy-500">Name</th>
                    <th className="px-3 py-2 text-left font-semibold text-navy-500">Email</th>
                    <th className="px-3 py-2 text-left font-semibold text-navy-500">Dept</th>
                    <th className="px-3 py-2 text-left font-semibold text-navy-500">Communicator</th>
                    <th className="px-3 py-2 text-left font-semibold text-navy-500">Category</th>
                    <th className="px-3 py-2 text-right font-semibold text-navy-500">Current Base</th>
                    <th className="px-3 py-2 text-right font-semibold text-navy-500">New Base</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 10).map((row, i) => (
                    <tr key={i} className="border-b border-surface-100 last:border-0">
                      <td className="px-3 py-2 text-navy-900 font-medium">{row.employeeName}</td>
                      <td className="px-3 py-2 text-navy-500">{row.employeeEmail}</td>
                      <td className="px-3 py-2 text-navy-500">{row.department || '—'}</td>
                      <td className="px-3 py-2 text-navy-500">{row.communicatorEmail || '—'}</td>
                      <td className="px-3 py-2 text-navy-500">{row.category}</td>
                      <td className="px-3 py-2 text-right text-navy-500">{row.currentBase?.toLocaleString() || '—'}</td>
                      <td className="px-3 py-2 text-right font-medium text-navy-900">{row.newBase?.toLocaleString() || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.rows.length > 10 && (
                <div className="px-3 py-2 bg-surface-50 text-xs text-navy-400 text-center">
                  + {preview.rows.length - 10} more rows
                </div>
              )}
            </div>

            {/* Matched existing employees hint */}
            <p className="text-xs text-navy-400">
              Rows matching an existing employee email will <span className="font-semibold text-navy-600">update</span> the record; new emails will <span className="font-semibold text-navy-600">create</span> new entries.
            </p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 text-center">
                <p className="text-2xl font-bold text-emerald-700">{result.created}</p>
                <p className="text-xs font-semibold text-emerald-600 mt-1">Created</p>
              </div>
              <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 text-center">
                <p className="text-2xl font-bold text-blue-700">{result.updated}</p>
                <p className="text-xs font-semibold text-blue-600 mt-1">Updated</p>
              </div>
            </div>
            {result.errors.length > 0 && (
              <div className="bg-red-50 rounded-xl border border-red-200 p-4">
                <p className="text-sm font-semibold text-red-700 mb-2">{result.errors.length} Error(s)</p>
                <ul className="text-xs text-red-600 space-y-1">
                  {result.errors.map((err, i) => (
                    <li key={i}>{err.row}: {err.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
